import { readable, writable } from 'svelte/store'
import { xor } from 'lodash'

function parseLocallyStored(item) {
  let locally_stored = false
	try { locally_stored = JSON.parse(localStorage.getItem(item)) }
	catch (error) { console.warn('Parse error in locally stored ' + item + ' - Error: ' + error) }
	return locally_stored
}

function dateIsToday(date_str) {
	let today = new Date()
	let date = new Date(date_str)
	return date.getFullYear() === today.getFullYear() &&
		date.getMonth() === today.getMonth() &&
		date.getDate() === today.getDate();
}

function createPreviewsNavFilters() {
	let state = {}
	let locally_stored = parseLocallyStored('previews_nav_filters')
	if (locally_stored) state = locally_stored || {}

	const { subscribe, set, update } = writable(state)
	return {
		subscribe,
		set:            (new_state) => set(new_state),
		showAll: 				() => set({}),
		reset: 					() => set({}),
		filterSelected: () => set({ selected: true }),
		filterSystem:   () => set({ system: true }),
		toggleSystem:   () => update(state => { if (state.system) { delete state.system } else { state.system = true } return state }),
		filterYours:    () => set({ user: true }),
		filterDropped:  () => set({ dropped: true }),
		filterUploaded: () => set({ uploaded: true }),
		filterStorefront: () => set({ storefront: true }),
		toggleYours:    () => update(state => { if (state.user) { delete state.user } else { state.user = true } return state }),
		filterTagged:   () => set({ tagged: true }),
		// filterUntagged: () => set({tagged: false})
		// filterUntagged: () => update(state => {state.tagged = false; return state}),
    toggleUntagged: () => update(state => { if (state.tagged === false) { state.tagged = true } else { state.tagged = false } delete state.user; return state })
	}
}

function createPreviewsTagsFilter() {
	let tags = []
	const { subscribe, set, update } = writable(tags)
	return {
		subscribe,
		toggleTag: (tag) => update(tags => xor(tags, [tag])),
		reset: () => set(tags = [])
	}
}
function createPreviewsCategoriesFilter() {
	let categories = []
	const { subscribe, set, update } = writable(categories)
	return {
		subscribe,
		toggleCategory: (category) => update(categories => xor(categories, [category])),
		reset: () => set(categories = [])
	}
}

function createPreviewsStorefrontsFilter() {
	let storefront = null; // Initialize with null
	const { subscribe, set, update } = writable(storefront);
	return {
		subscribe,
		toggleStorefront: (storefrontObj) => update(currentStorefront => {
			if (currentStorefront && currentStorefront.id === storefrontObj.id) {
				return null; // Reset to null if the same ID is toggled
			} else {
				return storefrontObj; // Set to the new storefront object
			}
		}),
		reset: () => set(storefront = null) // Reset to initial state
	}
}

function createModals() {
	let modals = []
	const { subscribe, set, update } = writable(modals)
	return {
		subscribe,
		register: (modal_id, persistence = false) => update(() => modals = [...modals, new Object({ id: modal_id, visible: false, persistent: persistence })]),
		isVisible: (modal_id) => modals.find(m => m.id === modal_id).visible,
		open: (modal_id) => {
			let prev_open = modals.find(m => m.visible === true)
			let new_open = modals.find(m => m.id === modal_id)
			if (prev_open) update(modals => { prev_open.visible = false; return modals })
			if (new_open) update(modals => { new_open.visible = true; return modals })
			else console.error("Modals - No modal with id: \"" + modal_id + "\" is registered with the modals store")
		},
		// have to specify a modal_id to close a persistent modal
		close: (modal_id) => {
			if (modal_id) {
				// When ID is provided, close only that specific modal (even if persistent)
				let target_modal = modals.find(m => m.id === modal_id)
				if (target_modal && target_modal.visible) {
					update(modals => { target_modal.visible = false; return modals })
				} else if (target_modal) {
					console.warn(`Modals - Modal with id "${modal_id}" is not currently visible`)
				} else {
					console.error(`Modals - No modal with id "${modal_id}" is registered`)
				}
			} else {
				// When no ID is provided, close any open non-persistent modal
				let prev_open = modals.find(m => m.visible === true)
				if (prev_open && !prev_open.persistent) {
					update(modals => { prev_open.visible = false; return modals })
				} else if (prev_open && prev_open.persistent) {
					console.warn("Modals - Cannot close persistent modal without specifying its ID")
				} else {
					console.warn("Modals - No modals to close!")
				}
			}
		}
	}
}

function createUser() {
	const { subscribe, set } = writable()
	return {
		subscribe,
		set: (new_user) => {
			const va = new Date(new_user.valid_until.replace(" ", "T"))
			const diff = va - new Date()
			new_user.days_to_expire = Math.floor((diff) / (1000 * 60 * 60 * 24))
			new_user.expiring = new_user.days_to_expire < 30
			new_user.expired = new_user.days_to_expire < 0
			new_user.full_name = new_user.first_name + " " + new_user.last_name
			set(new_user)
		}
	}
}

function createVisits() {
	let default_state = { first: false, second: false, customer: false, counter: 0 }
	
	// Safely get data from localStorage
	let locally_stored = parseLocallyStored('visits')
	let initial_state = locally_stored || default_state
	console.log('Visits - Current state: ' + JSON.stringify(initial_state))

	const { subscribe, set, update } = writable(initial_state)
	
	// Helper to safely save to localStorage
	const saveToLocalStorage = (state) => {
		try {
			localStorage.setItem('visits', JSON.stringify(state))
		} catch (error) {
			console.warn('Error saving visits to localStorage:', error)
		}
		return state
	}

	// Use get function to access current state
	const get = () => {
		let value
		subscribe(val => value = val)()
		return value || default_state
	}

	return {
		subscribe,
		set: (new_state) => set(new_state),
		new: () => update(state => {
			// Initialize state to default values if undefined
			state = state || default_state
			if (!state.first) state.first = Date.now()
			else if (!state.second && !dateIsToday(state.first)) state.second = Date.now()
			state.counter++
			return saveToLocalStorage(state)
		}),
		login: () => update(state => { 
			state = state || default_state
			state.customer = true
			return saveToLocalStorage(state)
		}),
		first: () => {
			const current = get()
			return !current.first || dateIsToday(current.first)
		},
		second: () => {
			const current = get()
			return current.second && dateIsToday(current.second)
		},
		multiple: () => {
			const current = get()
			return current.first != false && current.second != false && !dateIsToday(current.second)
		},
		count: () => get().counter,
		customer: () => get().customer,
	}
}

function createProLoadedFontsCounter() {
  const limit = 20
  const { subscribe, set, update } = writable({ loaded: 0, limit: limit });
  return {
    subscribe,
    increment: () => update(state => ({ 
      ...state, 
      loaded: state.loaded + 1 
    })),
    reset: () => set({ loaded: 0, limit: limit }),
    canLoad: () => {
      let current;
      subscribe(val => current = val)();
      return current.loaded < current.limit;
    },
    getCount: () => {
      let current;
      subscribe(val => current = val)();
      return current;
    }
  };
}

function createApiLoadingState() {
  const { subscribe, set, update } = writable({
    loading: false,
    error: null
  })

  return {
    subscribe,
    // Set loading state
    setLoading: () => set({ loading: true, error: null }),
    // Set success state
    setSuccess: () => set({ loading: false, error: null }),
    // Set error state
    setError: (error) => set({ loading: false, error }),
    // Reset to initial state
    reset: () => set({ loading: false, error: null })
  }
}

// Environment
export const env = readable(window.location.href.includes('it') ? 'production' : 'development')
export const server_path = readable(window.location.href.includes('it') ? 'https://server.wordmark.it/' : 'https://server.wordmark.test/')
export const client_path = readable(window.location.href.includes('it') ? 'https://wordmark.it/' : 'https://wordmark.test/')
export const generator_path = readable(window.location.href.includes('it') ? 'https://generator.wordmark.it/' : 'https://generator.wordmark.test/')

// Visits, used to count visits
export const visits = createVisits()

// Theme, chosen theme can be light, dark, or auto
export const chosen_theme = writable('light')
export const dark_theme = writable(false)

// Hero
export const hero_expanded = writable(true)

// Inserts
export const inserts_enabled = writable(true)

// Buy Modal Counter
export const buy_modal_counter = writable(Number(localStorage.getItem("buy_modal_counter")) || 0)
buy_modal_counter.subscribe(val => localStorage.setItem("buy_modal_counter", val))

// Buy Modal Initiator, tracks which feature triggered the buy modal
export const buy_modal_initiator = writable()

// Subscriptions
export const new_subscription_id = writable()

export const pro_monthly_sale_details = writable()
export const pro_monthly_price_details = writable()
export const pro_yearly_sale_details = writable()
export const pro_yearly_price_details = writable()

export const business_monthly_price_details = writable()
export const business_yearly_price_details = writable()
export const business_monthly_sale_details = writable()
export const business_yearly_sale_details = writable()

export const yearly_upsell_from_pro_to_business_estimate = writable()
export const monthly_upsell_from_pro_to_business_estimate = writable()
export const currency_sale = writable(false)

export const can_upgrade_from_pro_to_business = writable(false)
export const pro_subscription_deactivated = writable(false)
export const business_subscription_deactivated = writable(false)
export const business_subscription_active = writable(false)
export const subscription_email_mismatch = writable() // object with current_email and subscription_email

// Preview Width used to detect unrendered fonts
export const default_preview_width = writable()

// Entered Preview Text
export const text_entered = writable('')
export const text_previewed = writable('')
export const text_caseapplied = writable(undefined)
export const selected_text_case = writable(undefined)
export const text_entered_once = writable(false)

// Preview Mode, can be words, paragraphs, or letters
export const preview_mode = writable('words')

// Previews Filters
export const previews_nav_filters = createPreviewsNavFilters()
export const previews_search_filter = writable('')
export const previews_weight_filter = writable('')
export const previews_tags_filter = createPreviewsTagsFilter()
export const previews_tags_filter_operator = writable('or')
export const previews_categories_filter = createPreviewsCategoriesFilter()
export const previews_storefronts_filter = createPreviewsStorefrontsFilter()

// Previews Display Options
export const previews_font_size = writable(localStorage.getItem('previews_font_size') || 3)
export const previews_min_font_size = writable(0)
export const previews_max_font_size = writable(10)
export const previews_tracking = writable(Number(localStorage.getItem('previews_tracking')) || 0)
export const previews_leading = writable(Number(localStorage.getItem('previews_leading')) || 1.2)
export const previews_sort_order = writable('ascending')

// Previews
export const previews_are_highlighted = writable(false)
export const previews_are_google_fonts = writable(false)
export const previews_are_zoomed = writable(false)
export const previews_scrolled_up = writable(false)
export const previews_scrolled_down = writable(false)
export const previews_last_scroll_position = writable()

// User Data
export const user = createUser()
export const user_valid = writable(false)
export const user_subscription = writable()

// User Data Stores (reverted to simple writable stores)
export const user_font_tags = writable([])
export const user_saved_selections = writable([])
export const user_storefronts = writable([])
export const user_uploaded_fonts = writable([])

// API Loading States (separate from data stores)
export const user_font_tags_loading_state = createApiLoadingState()
export const user_saved_selections_loading_state = createApiLoadingState()
export const user_storefronts_loading_state = createApiLoadingState()
export const user_uploaded_fonts_loading_state = createApiLoadingState()

// AI Categorization
export const user_categorized_families = writable()

// Selections
export const user_last_saved_selection_form_data = writable()
export const selection_to_delete = writable()

// Tags
export const tags_are_being_edited = writable(false)
export const tag_to_rename = writable()
export const tag_to_remove = writable()
export const tag_to_delete = writable()
export const font_id_to_tag = writable()

// Storefronts
export const storefront_id_to_edit = writable()
export const storefront_to_remove = writable()
export const storefront_to_delete = writable()
export const storefront_url_to_open = writable()
export const storefront_just_created = writable(false)

// Detected Fonts
export const system_fonts = writable()
export const extension_fonts = writable()
export const local_fonts = writable()
export const google_fonts = writable()
export const dropped_font_files = writable([])
export const detected_fonts = writable()

export const just_switched_to_local_fonts = writable(false)
export const font_upload_progress = writable()
export const font_detection_in_progress = writable(true)
export const dropzone_visible = writable(false);

export const hobby_font_limit = writable(400)
export const user_installed_fonts_count = writable(0)

export const modals = createModals()
export const demos = { 
  subscribe: writable(false).subscribe,
  started: () => false, 
  valid: () => false,
  start: () => {} 
}

// Flash Messages
export const flash_content = writable()
export const flash_shown_copy_paste = writable(false)

// Modal mode for upload/storefront modal
export const upload_storefront_modal_mode = writable('addToStorefront')
export const max_upload_file_size = writable(5 * 1024 * 1024) // 5MB in bytes (same as server limit)
export const loaded_uploaded_fonts_counter = createProLoadedFontsCounter()